import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Loader2, UserPlus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import { authApi } from '@/features/auth/api'
import { PasswordStrength, isPasswordStrong } from '@/components/PasswordStrength'

const errMsg = (err: unknown, fallback = 'Signup failed.') => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function SignupPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  // Invite-driven signup arrives as /signup?invite=<token>&email=<invited email>.
  // In that case: email is locked to the invited address, the backend binds a
  // membership for the invited tenant, and the response includes a ready-to-use
  // App JWT so the invitee can skip straight to onboarding.
  const inviteToken = params.get('invite') ?? ''
  const invitedEmail = params.get('email') ?? ''

  const [name, setName] = useState('')
  const [email, setEmail] = useState(invitedEmail)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (invitedEmail) setEmail(invitedEmail)
  }, [invitedEmail])

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    if (!isPasswordStrong(password)) {
      setError('Password does not meet all the requirements listed below.')
      return
    }
    setBusy(true)
    try {
      const r = await authApi.signup({
        email,
        password,
        name,
        ...(inviteToken ? { inviteToken } : {}),
      })

      // Invite signup: backend returns a ready App JWT + a redirect hint.
      if (r.token) {
        tokenManager.set(r.token)
        if (r.userId) localStorage.setItem('userId', String(r.userId))
        if (r.email) localStorage.setItem('userEmail', r.email)
        if (r.name) localStorage.setItem('userName', r.name)
        navigate(r.redirect || '/dashboard', { replace: true })
        return
      }

      setSuccess(
        r.message ??
          'Account created. Please check your email for a verification link before signing in.',
      )
      setTimeout(() => navigate('/login'), 4000)
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">
          {inviteToken ? 'Finish joining the workspace' : 'Create your account'}
        </CardTitle>
        <p className="text-center text-sm text-muted-foreground">
          {inviteToken
            ? 'Set a password for your new account to accept the invitation.'
            : 'Start your Bokapsys workspace'}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        {success ? (
          <Alert>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        ) : null}
        <form onSubmit={submit} className="space-y-3">
          <FormField
            label="Full name"
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            autoComplete="name"
          />
          <FormField
            label="Email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            autoComplete="email"
            disabled={!!inviteToken}
            readOnly={!!inviteToken}
            hint={inviteToken ? 'Email is locked to the invited address.' : undefined}
          />
          <FormField
            label="Password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.currentTarget.value)}
            autoComplete="new-password"
          />
          <PasswordStrength password={password} />
          <FormField
            label="Confirm password"
            type="password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.currentTarget.value)}
            autoComplete="new-password"
            error={
              confirm && confirm !== password ? 'Passwords do not match.' : undefined
            }
          />
          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : <UserPlus className="size-4" />}
            {inviteToken ? 'Join workspace' : 'Create account'}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link
            to={inviteToken ? `/login?invite=${encodeURIComponent(inviteToken)}` : '/login'}
            className="font-medium text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </CardContent>
    </Card>
  )
}
